import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["search", "results", "selected", "hiddenInputs"]
  static values = {
    searchUrl: String,
    selected: Array,
    fieldName: { type: String, default: "book_ids[]" }
  }

  connect() {
    this.searchTimeout = null
    this.renderSelected()
  }

  search() {
    const query = this.searchTarget.value.trim()

    clearTimeout(this.searchTimeout)

    if (query.length < 2) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.add("hidden")
      return
    }

    this.searchTimeout = setTimeout(() => this.performSearch(query), 300)
  }

  async performSearch(query) {
    try {
      const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Search failed")

      const books = await response.json()
      this.renderResults(books)
    } catch (error) {
      console.error("Book search error:", error)
      this.resultsTarget.innerHTML = `<div class="p-3 text-sm text-red-600">Search failed. Please try again.</div>`
      this.resultsTarget.classList.remove("hidden")
    }
  }

  renderResults(books) {
    if (books.length === 0) {
      this.resultsTarget.innerHTML = `<div class="p-3 text-sm text-beige-500 font-fira">No books found</div>`
      this.resultsTarget.classList.remove("hidden")
      return
    }

    const selectedIds = this.selectedValue.map(b => b.id)

    const itemsHtml = books.map(book => {
      const isSelected = selectedIds.includes(book.id)
      return `
        <label class="flex items-center gap-3 p-2 hover:bg-beige-50 transition-colors ${isSelected ? 'opacity-60' : 'cursor-pointer'}">
          <input type="checkbox"
                 class="form-checkbox flex-shrink-0 h-4 w-4"
                 data-book-id="${book.id}"
                 data-book-title="${this.escapeAttr(book.title)}"
                 data-book-author="${this.escapeAttr(book.author_names || '')}"
                 data-book-cover="${this.escapeAttr(book.cover_url || '')}"
                 ${isSelected ? 'disabled checked' : ''}>
          <div class="w-10 h-14 flex-shrink-0 bg-beige-200 rounded overflow-hidden">
            ${book.cover_url ? `<img src="${this.escapeAttr(book.cover_url)}" alt="" class="w-full h-full object-contain">` : ''}
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-beige-900 truncate">${this.escapeHtml(book.title)}</p>
            ${book.author_names ? `<p class="text-xs text-beige-500 font-fira truncate">${this.escapeHtml(book.author_names)}</p>` : ''}
          </div>
          ${isSelected ? '<span class="text-xs text-green-600 font-fira flex-shrink-0">Added</span>' : ''}
        </label>
      `
    }).join("")

    const footerHtml = `
      <div class="sticky bottom-0 bg-white border-t border-beige-200 p-2 flex items-center justify-between gap-2">
        <span class="text-xs text-beige-500 font-fira">Check boxes, then click Add</span>
        <button type="button" class="btn-primary text-sm px-3 py-1.5" data-action="book-selector#addSelected">
          Add Selected
        </button>
      </div>
    `

    this.resultsTarget.innerHTML = itemsHtml + footerHtml
    this.resultsTarget.classList.remove("hidden")
  }

  addSelected(event) {
    event.preventDefault()
    event.stopPropagation()

    const checkboxes = this.resultsTarget.querySelectorAll('input[type="checkbox"]:checked:not(:disabled)')
    if (checkboxes.length === 0) return

    const currentSelected = [...this.selectedValue]
    checkboxes.forEach(cb => {
      const book = {
        id: parseInt(cb.dataset.bookId),
        title: cb.dataset.bookTitle,
        author_names: cb.dataset.bookAuthor,
        cover_url: cb.dataset.bookCover
      }
      if (!currentSelected.find(b => b.id === book.id)) {
        currentSelected.push(book)
      }
    })

    this.selectedValue = currentSelected
    this.renderSelected()

    // Clear search & results
    this.searchTarget.value = ""
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("hidden")
  }

  remove(event) {
    const bookId = parseInt(event.currentTarget.dataset.bookId)
    this.selectedValue = this.selectedValue.filter(b => b.id !== bookId)
    this.renderSelected()
  }

  renderSelected() {
    const books = this.selectedValue

    if (books.length === 0) {
      this.selectedTarget.innerHTML = `
        <div class="text-center py-8 text-beige-400 font-fira text-sm">
          No books selected. Use the search above to add books.
        </div>
      `
      this.hiddenInputsTarget.innerHTML = ""
      return
    }

    // Render selected books
    const html = books.map(book => `
      <div class="flex items-center gap-3 p-2 bg-beige-50 rounded-lg group">
        <div class="w-10 h-14 flex-shrink-0 bg-beige-200 rounded overflow-hidden">
          ${book.cover_url ? `<img src="${this.escapeAttr(book.cover_url)}" alt="" class="w-full h-full object-cover">` : ''}
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-beige-900 truncate">${this.escapeHtml(book.title)}</p>
          ${book.author_names ? `<p class="text-xs text-beige-500 font-fira truncate">${this.escapeHtml(book.author_names)}</p>` : ''}
        </div>
        <button type="button"
                class="p-1 text-beige-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                data-action="book-selector#remove"
                data-book-id="${book.id}"
                title="Remove">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `).join("")

    this.selectedTarget.innerHTML = html

    // Render hidden inputs
    const hiddenHtml = books.map(book =>
      `<input type="hidden" name="${this.fieldNameValue}" value="${book.id}">`
    ).join("")

    this.hiddenInputsTarget.innerHTML = hiddenHtml
  }

  hideResults(event) {
    // Don't hide if clicking inside results
    if (this.resultsTarget.contains(event.relatedTarget)) return

    setTimeout(() => {
      this.resultsTarget.classList.add("hidden")
    }, 200)
  }

  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  escapeAttr(text) {
    if (!text) return ""
    return text.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
